// @ts-nocheck
import {
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  Timestamp,
  GeoPoint,
  DocumentReference,
  QueryDocumentSnapshot,
  collection,
  query,
  where,
  QueryConstraint,
  CollectionReference,
} from 'firebase/firestore'

import { db } from '@/firebase/config'

/** Recursively convert Firestore Timestamps, GeoPoints, and DocumentReferences to plain JS types */
function convertFromFirestore(value: any): any {
  if (value === null || value === undefined) return value
  if (value instanceof Timestamp) return value.toDate()
  if (value instanceof GeoPoint) return { lat: value.latitude, lng: value.longitude }
  if (value instanceof DocumentReference) return { __ref__: value.path }
  if (Array.isArray(value)) return value.map(convertFromFirestore)
  if (value && typeof value === 'object') {
    const result: Record<string, any> = {}
    for (const key in value) result[key] = convertFromFirestore(value[key])
    return result
  }
  return value
}

/** Recursively convert JS Dates and ref placeholders back into Firestore types.
 *  Strips undefined values so we never pass them to updateDoc/setDoc. */
function convertToFirestore(value: any, db?: any): any {
  // Preserve nulls (allowed by Firestore), but drop undefined at parent level
  if (value === null) return null
  if (value === undefined) return undefined

  // Primitives
  if (typeof value !== 'object' || value instanceof Timestamp || value instanceof GeoPoint) {
    return value instanceof Date ? Timestamp.fromDate(value) : value
  }

  // Dates
  if (value instanceof Date) return Timestamp.fromDate(value)

  // DocumentReference placeholder
  if (value && value.__ref__ && db) return doc(db, value.__ref__)

  // Arrays: convert and drop undefined entries
  if (Array.isArray(value)) {
    return value.map((v) => convertToFirestore(v, db)).filter((v) => v !== undefined)
  }

  // Objects: convert and omit undefined keys
  const result: Record<string, any> = {}
  for (const key in value) {
    const converted = convertToFirestore(value[key], db)
    if (converted !== undefined) {
      result[key] = converted
    }
  }
  return result
}

export const firestoreService = {
  async get(docRef: DocumentReference) {
    const snap = await getDoc(docRef)
    if (!snap.exists()) return null
    return convertFromFirestore({ id: snap.id, ...snap.data() })
  },

  async list(colRef: CollectionReference) {
    const snap = await getDocs(colRef)
    return snap.docs.map((docSnap: QueryDocumentSnapshot) =>
      convertFromFirestore({ id: docSnap.id, ...docSnap.data() }),
    )
  },

  async query<T>(
    colRef: CollectionReference,
    whereConditions: [string, FirebaseFirestore.WhereFilterOp, any][],
  ): Promise<T[]> {
    const constraints: QueryConstraint[] = whereConditions.map(([field, op, value]) =>
      where(field, op, value instanceof Date ? Timestamp.fromDate(value) : value),
    )
    const q = query(colRef, ...constraints)
    const snap = await getDocs(q)
    return snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as T)
  },

  async add(colRef: CollectionReference, data: any, dbRef = db) {
    const ref = await addDoc(colRef, convertToFirestore(data, dbRef))
    return ref.id
  },

  async set(docRef: DocumentReference, data: unknown, options = {}, dbRef = db) {
    await setDoc(docRef, convertToFirestore(data, dbRef), options)
  },

  async update(docRef: DocumentReference, data: unknown, dbRef = db) {
    await updateDoc(docRef, convertToFirestore(data, dbRef))
  },

  async delete(docRef: DocumentReference) {
    await deleteDoc(docRef)
  },

  convertFromFirestore,
  convertToFirestore,
}
